/// 担当者 (app_accounts) の型。ADR 20260528 / PR-ACC-1。
///
/// `current_role` は `app_account_current_role` View 由来 (event sourcing の最新)。
/// 認可には使わず表示用 (認可は Auth0/JWT claim が真実)。

export type AccountStatus = 'invited' | 'active' | 'inactive';
/// 担当者に記録される role (ADR 20260605、backend `Role` と同値)。
export type AccountRole = 'super_admin' | 'org_admin' | 'member' | 'readonly';

const KNOWN_ACCOUNT_ROLES: readonly AccountRole[] = [
  'super_admin',
  'org_admin',
  'member',
  'readonly',
];

/// API / 旧データ由来の role 文字列を既知の AccountRole に正規化する。
/// 移行期の後方互換として legacy `'admin'` を `'super_admin'` に寄せる (pickAppRole と同じ。
/// cleanup PR #402 で除去予定)。未知の値は `null` を返す (呼び出し側で raw / フォールバック表示)。
/// current_role は API から生文字列で来るため、型 (AccountRole) の外の値が混ざり得る点に対応する。
export function normalizeAccountRole(role: string | null | undefined): AccountRole | null {
  if (!role) return null;
  const mapped = role === 'admin' ? 'super_admin' : role;
  return (KNOWN_ACCOUNT_ROLES as readonly string[]).includes(mapped)
    ? (mapped as AccountRole)
    : null;
}

/// AccountRole の表示ラベル (日本語)。メンバー一覧 / ロール変更 / 招待 UI で共有する。
export const ACCOUNT_ROLE_LABELS: Record<AccountRole, string> = {
  super_admin: 'スーパー管理者',
  org_admin: '組織管理者',
  member: 'メンバー',
  readonly: '閲覧のみ',
};

/// current_role の生文字列 (null / legacy 'admin' / 未知含む) を表示ラベルに変換する。
/// 正規化して既知ラベルを引き、引けなければ raw を出す (未解決は '—')。
export function accountRoleLabel(role: string | null | undefined): string {
  if (!role) return '—';
  const norm = normalizeAccountRole(role);
  return norm ? ACCOUNT_ROLE_LABELS[norm] : role;
}

export interface Account {
  id: string;
  display_name: string;
  status: AccountStatus;
  current_role: AccountRole | null;
  primary_email: string | null;
}

/// 顧客DBの担当者候補。通常 UI ではメールやロールを表示しない。
export interface AssignableAccount {
  id: string;
  display_name: string;
  status: AccountStatus;
}

/// 招待 / 無効化 / 再有効化のレスポンス (本体のみ)。
export interface AccountSummary {
  id: string;
  display_name: string;
  status: AccountStatus;
}

export interface InviteAccountRequest {
  email: string;
  display_name?: string;
  /// 付与する Auth0 role_id 群 (複数ロール対応)。CRM 主ロールは backend が privilege 最大で導出する。
  role_ids: string[];
  /// 招待先 Auth0 organization と招待 URL 生成元 SPA client。frontend が
  /// listOrganizations / listClients から解決して供給する (既存の Auth0 招待 UI と同方式)。
  organization_id: string;
  client_id: string;
}

/// JIT 同期 (`POST /api/me/sync`) のレスポンス (ADR 20260528 §7 / ACC-2)。
/// ログイン直後に identity を populate し、`invited → active` 遷移と role の fact log を
/// 記録した結果のスナップショット。
export interface AccountSync {
  account_id: string;
  status: AccountStatus;
  current_role: AccountRole | null;
  email_count: number;
  identity_count: number;
}
